/**
 * ReportBriefPage — /reports/:slug — one public, indexable page per report with
 * TourismIntel's original editorial brief (3/9 SEO expansion). Fixed structure:
 * header → at a glance → scope → key numbers → why it matters → methodology →
 * official-source box → related + Pulse links → share + signup CTA.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, RefreshCw, Linkedin, Link2, Calendar, Lock, FileText, ChevronRight, BarChart3, FileCode2,
} from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import ReportCard from "../components/ReportCard";
import ReportsSignupCta from "../components/reports/ReportsSignupCta";
import { API } from "../config";
import { setAlternate, setCanonical, setDescription, setJsonLd, setNoIndex, SITE_URL } from "../lib/seo";

const fmtDate = (d, precision) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (precision === "year") return String(dt.getUTCFullYear());
    if (precision === "month") return dt.toLocaleDateString("en-US", { year: "numeric", month: "long", timeZone: "UTC" });
    return dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
};

function Section({ title, icon: Icon, children, c, testid }) {
  return (
    <section className="mt-10" data-testid={testid}>
      <h2 className={`text-base md:text-lg font-semibold mb-3 flex items-center gap-2 ${c.text}`}>
        {Icon && <Icon className={`w-4 h-4 ${c.accent}`} />}{title}
      </h2>
      {children}
    </section>
  );
}

export default function ReportBriefPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [gone, setGone] = useState(false);
  const [dm] = useDarkMode();

  const isMd = /\.md$/i.test(slug || "");

  useEffect(() => {
    if (isMd) { window.location.replace(`${API}/reports/${slug}`); return; }
    setData(null); setNotFound(false); setGone(false);
    axios.get(`${API}/reports/by-slug/${slug}`).then(({ data: d }) => setData(d))
      .catch((e) => { setNotFound(true); setGone(e?.response?.status === 404); });
  }, [slug, isMd]);

  useEffect(() => (gone ? setNoIndex() : undefined), [gone]);

  useEffect(() => {
    if (!data) return undefined;
    const { report: r, brief: b, category: cat } = data;
    const title = r.clean_title || r.title;
    document.title = `${title} — key findings & summary | TourismIntel Reports`;
    const desc = ((b?.at_a_glance || []).join(" ") || r.excerpt || "").slice(0, 300);
    const c1 = setDescription(desc);
    setCanonical(`/reports/${r.slug}`);
    const ld = {
      "@context": "https://schema.org", "@type": "Report", headline: title, name: title,
      url: `${SITE_URL}/reports/${r.slug}`, inLanguage: "en", isBasedOn: r.url,
      publisher: { "@type": "Organization", name: r.organisation || "Unknown" },
      author: { "@type": "Organization", name: "TourismIntel", url: SITE_URL },
      description: b?.abstract || r.excerpt, articleSection: cat?.label,
      ...(r.published_date ? { datePublished: r.published_date } : {}),
      ...(b?.updated_at || r.published_date ? { dateModified: b?.updated_at || r.published_date } : {}),
    };
    const crumbs = {
      "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Reports", item: `${SITE_URL}/reports` },
        { "@type": "ListItem", position: 2, name: cat?.label, item: `${SITE_URL}/reports/category/${cat?.slug}` },
        { "@type": "ListItem", position: 3, name: title, item: `${SITE_URL}/reports/${r.slug}` },
      ],
    };
    const c2 = setJsonLd("report-jsonld", ld);
    const c3 = setJsonLd("report-breadcrumb-jsonld", crumbs);
    const c4 = setAlternate("text/markdown", `${SITE_URL}/api/reports/${r.slug}.md`);
    return () => { c1(); c2(); c3(); c4(); };
  }, [data]);

  const c = dm
    ? { bg: "bg-[#0f1718]", card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-100", muted: "text-slate-500", sub: "text-slate-300", accent: "text-cyan-400", chip: "bg-white/5 text-slate-300" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white", border: "border-[#e7e8e9]", text: "text-[#191c1d]", muted: "text-[#6b7a7d]", sub: "text-[#3b494c]", accent: "text-[#006874]", chip: "bg-[#eef3f4] text-[#3b494c]" };

  const shareUrl = `${API}/preview/report/${slug}`;
  const r = data?.report; const b = data?.brief; const cat = data?.category;
  const access = b?.access || (r?.access && r.access !== "Not specified" ? r.access : null);

  return (
    <div className={`min-h-screen flex flex-col ${c.bg}`}>
      <PublicPageNav active="reports" />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10" data-testid="report-brief-page">
        {notFound ? (
          <div className={`text-center py-20 ${c.muted}`} data-testid="report-brief-not-found">
            <p className="mb-3">Report not found.</p>
            <Link to="/reports" className={`underline ${c.accent}`}>Back to all reports</Link>
          </div>
        ) : !data ? (
          <div className="flex justify-center py-20"><RefreshCw className={`w-6 h-6 animate-spin ${c.muted}`} /></div>
        ) : (
          <article>
            <nav className={`flex items-center flex-wrap gap-1 text-xs mb-6 ${c.muted}`} aria-label="Breadcrumb" data-testid="report-breadcrumb">
              <Link to="/reports" className="hover:underline">Reports</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/reports/category/${cat.slug}`} className="hover:underline" data-testid="report-breadcrumb-category">{cat.label}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className={`truncate max-w-[50vw] ${c.sub}`}>{r.clean_title}</span>
            </nav>

            <div className={`flex items-center gap-2 mb-3 text-xs font-bold uppercase tracking-wider ${c.accent}`} data-testid="report-brief-org">
              <FileText className="w-4 h-4" />
              {data.organisation_hub
                ? <Link to={`/reports/organisation/${data.organisation_hub}`} className="hover:underline" data-testid="report-brief-org-link">{r.organisation}</Link>
                : <span>{r.organisation}</span>}
              <span className={`font-normal normal-case tracking-normal ${c.muted}`}>· {b?.report_type || r.report_type || "Report"}</span>
            </div>
            <h1 className={`editorial-heading text-4xl sm:text-5xl lg:text-6xl font-medium mb-4 leading-tight ${c.text}`} data-testid="report-brief-title">
              {r.clean_title}
            </h1>
            <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 mb-8 text-sm ${c.muted}`}>
              {r.published_date && <span className="inline-flex items-center gap-1" data-testid="report-brief-date"><Calendar className="w-3.5 h-3.5" /> {fmtDate(r.published_date, r.published_date_precision)}</span>}
              <Link to={`/reports/category/${cat.slug}`} className={`px-2 py-0.5 rounded-full text-xs ${c.chip}`} data-testid="report-brief-category">{cat.label}</Link>
              {access && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${c.chip}`} data-testid="report-brief-access">{/free/i.test(access) ? null : <Lock className="w-3 h-3" />}{access}</span>}
              {b?.geography && <span className={`px-2 py-0.5 rounded-full text-xs ${c.chip}`}>{b.geography}</span>}
              {b?.period && <span className={`px-2 py-0.5 rounded-full text-xs ${c.chip}`}>{b.period}</span>}
              <span className="flex items-center gap-1 ml-auto">
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer"
                  title="Share on LinkedIn" data-testid="report-share-linkedin"
                  className={`p-1.5 rounded-full transition-colors ${dm ? "text-slate-400 hover:text-cyan-300 hover:bg-white/5" : "text-[#6b7a7d] hover:text-[#006874] hover:bg-[#f0f5f5]"}`}>
                  <Linkedin className="w-4 h-4" />
                </a>
                <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Share link copied — paste it on LinkedIn for a rich card"); }}
                  title="Copy share link" data-testid="report-share-copy"
                  className={`p-1.5 rounded-full transition-colors ${dm ? "text-slate-400 hover:text-cyan-300 hover:bg-white/5" : "text-[#6b7a7d] hover:text-[#006874] hover:bg-[#f0f5f5]"}`}>
                  <Link2 className="w-4 h-4" />
                </button>
              </span>
            </div>

            {!b ? (
              <div className={`rounded-2xl border p-6 ${c.card} ${c.border}`} data-testid="report-brief-pending">
                <p className={`text-base leading-relaxed ${c.sub}`}>{r.summary || r.excerpt}</p>
                <p className={`text-xs mt-4 ${c.muted}`}>
                  {data.brief_status === "failed" ? "The full TourismIntel brief could not be generated from the public page yet." : "Full TourismIntel brief in preparation — check back shortly."}
                </p>
              </div>
            ) : (
              <>
                <section className={`rounded-2xl border p-6 ${c.card} ${c.border}`} data-testid="report-brief-glance">
                  <h2 className={`text-base md:text-lg font-semibold mb-3 ${c.text}`}>At a glance</h2>
                  <ul className="space-y-2.5">
                    {b.at_a_glance.map((x, i) => (
                      <li key={i} className={`flex gap-3 text-[15px] leading-relaxed ${c.sub}`}>
                        <span className={`mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dm ? "bg-cyan-400" : "bg-[#006874]"}`} />{x}
                      </li>
                    ))}
                  </ul>
                </section>

                <Section title="What the report covers" c={c} testid="report-brief-scope">
                  <p className={`text-[15px] leading-relaxed ${c.sub}`}>{b.scope}</p>
                </Section>

                {b.key_findings?.length > 0 && (
                  <Section title="Key findings" c={c} testid="report-brief-findings">
                    <div className="space-y-4">
                      {b.key_findings.map((p, i) => (
                        <p key={i} className={`text-[15px] leading-relaxed ${c.sub}`} data-testid={`report-finding-${i}`}>{p}</p>
                      ))}
                    </div>
                  </Section>
                )}

                {b.key_numbers?.length > 0 && (
                  <Section title="Key numbers" icon={BarChart3} c={c} testid="report-brief-numbers">
                    <div className={`rounded-xl border overflow-hidden ${c.border}`}>
                      <table className="w-full text-sm">
                        <thead className={dm ? "bg-white/5" : "bg-[#eef3f4]"}>
                          <tr className={`text-left text-xs uppercase tracking-wider ${c.muted}`}>
                            <th className="px-4 py-2 font-semibold">Metric</th><th className="px-4 py-2 font-semibold">Value</th><th className="px-4 py-2 font-semibold hidden sm:table-cell">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {b.key_numbers.map((k, i) => (
                            <tr key={i} className={`border-t ${c.border} ${c.sub}`} data-testid={`report-key-number-${i}`}>
                              <td className="px-4 py-2.5">{k.metric}</td>
                              <td className={`px-4 py-2.5 font-semibold whitespace-nowrap ${c.text}`}>{k.value}</td>
                              <td className={`px-4 py-2.5 text-xs hidden sm:table-cell ${c.muted}`}>{k.note}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className={`text-xs mt-2 ${c.muted}`}>Figures as published in the source; forecasts and survey results are labelled as such in the note.</p>
                  </Section>
                )}

                {b.why_it_matters?.length > 0 && (
                  <Section title="Why it matters" c={c} testid="report-brief-why">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {b.why_it_matters.map((w, i) => (
                        <div key={i} className={`rounded-xl border p-4 ${c.card} ${c.border}`}>
                          <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${c.accent}`}>{w.audience}</p>
                          <p className={`text-sm leading-relaxed ${c.sub}`}>{w.text}</p>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                <Section title="Methodology and limits" c={c} testid="report-brief-methodology">
                  <p className={`text-[15px] leading-relaxed ${c.sub}`}>{b.methodology}</p>
                  {b.partial_source && (
                    <p className={`text-xs mt-3 px-3 py-2 rounded-lg ${dm ? "bg-amber-400/10 text-amber-300" : "bg-amber-50 text-amber-800"}`} data-testid="report-brief-partial">
                      {b.source_kind === "none"
                        ? "The source document could not be retrieved when this brief was written: it is based on the report's public metadata and abstract only. Check the official source for the full findings."
                        : "This brief is based on the publicly available summary of the report only (the full document is gated or was not reachable)."}
                    </p>
                  )}
                </Section>
              </>
            )}

            <section className={`mt-10 rounded-2xl p-6 ${dm ? "bg-[#0c1011]" : "bg-[#0f2529]"}`} style={{ border: "1px solid rgba(143,212,220,0.18)" }} data-testid="report-brief-source-box">
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#8fd4dc" }}>Official source</p>
              <p className="text-sm leading-relaxed" style={{ color: "#a9c2c6" }}>
                The report is © {r.organisation}. This brief is an original editorial summary by TourismIntel — it quotes only figures published in the source and never reproduces the document.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid="report-brief-official-link"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors" style={{ background: "#8fd4dc", color: "#001f24" }}>
                  Read the original report <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <span className="text-xs" style={{ color: "#a9c2c6" }}>Access: {access || "Not specified"}</span>
                <a href={`${API}/reports/${r.slug}.md`} target="_blank" rel="noopener noreferrer" data-testid="report-brief-md-link"
                  className="ml-auto inline-flex items-center gap-1 text-xs hover:underline" style={{ color: "#a9c2c6" }}>
                  <FileCode2 className="w-3.5 h-3.5" /> Markdown version
                </a>
              </div>
            </section>

            {b?.pulse_links?.length > 0 && (
              <Section title="Check the official statistics on Pulse" icon={BarChart3} c={c} testid="report-brief-pulse-links">
                <ul className="space-y-2">
                  {b.pulse_links.map((p) => (
                    <li key={p.key}>
                      <Link to={p.href} className={`inline-flex items-center gap-1.5 text-sm font-semibold hover:underline ${c.accent}`} data-testid={`report-pulse-link-${p.key}`}>
                        {p.label} <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {data.related?.length > 0 && (
              <Section title="Related reports" c={c} testid="report-brief-related">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.related.map((rr) => <ReportCard key={rr.id} report={rr} darkMode={dm} compact />)}
                </div>
              </Section>
            )}

            <div className="mt-10"><ReportsSignupCta dm={dm} variant="brief" testid="report-brief-signup-cta" /></div>

            <div className={`mt-8 flex flex-wrap items-center justify-between gap-2 text-xs ${c.muted}`}>
              <Link to="/reports" className={`inline-flex items-center gap-1 hover:underline ${c.accent}`} data-testid="report-brief-back"><ArrowLeft className="w-3.5 h-3.5" /> All reports</Link>
              {b?.updated_at && <span data-testid="report-brief-updated">Brief updated {fmtDate(b.updated_at)}{b.edited_by_admin ? " · reviewed by the editors" : ""}</span>}
            </div>
          </article>
        )}
      </main>
      <PublicPageFooter dark={dm} />
    </div>
  );
}
